import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { ProviderAvailability } from '@types';

const TIME_BLOCKS = ['now', 'today', 'tomorrow'] as const;
export type TimeBlock = (typeof TIME_BLOCKS)[number];
export type GroupedAvailabilitiesByTimeBlock = Record<
  TimeBlock,
  ProviderAvailability[]
>;

function isTimeBlock(value: string): value is TimeBlock {
  return TIME_BLOCKS.includes(value as TimeBlock);
}

export function useParseTimeBlockSearchParam() {
  const [searchParams] = useSearchParams();

  const param = searchParams.get('timeBlock');

  if (param && isTimeBlock(param)) {
    return param;
  }

  return null;
}

export function groupProviderAvailabilitiesByTimeBlock(
  availabilities: ProviderAvailability[],
) {
  const today = dayjs();
  const thirtyMinutesFromNow = today.add(31, 'minute');
  const tomorrow = today.add(1, 'day');

  return availabilities.reduce<GroupedAvailabilitiesByTimeBlock>(
    (groups, availability) => {
      const { provider, timeSlotCollection } = availability;

      if (timeSlotCollection.length === 0) {
        return groups;
      }

      timeSlotCollection.forEach((timeSlot) => {
        const { date, times } = timeSlot;
        const timeSlotDay = dayjs(date);

        if (timeSlotDay.isSame(tomorrow, 'day')) {
          groups.tomorrow.push({
            provider,
            timeSlotCollection: [timeSlot],
          });

          return;
        }

        const availabilityBeforeThirtyMinutes: ProviderAvailability = {
          provider,
          timeSlotCollection: [
            {
              date: timeSlot.date,
              times: [],
            },
          ],
        };

        const availabilityAfterThirtyMinutes: ProviderAvailability = {
          provider,
          timeSlotCollection: [
            {
              date: timeSlot.date,
              times: [],
            },
          ],
        };

        times.forEach((time) => {
          const dateTime = dayjs(`${date}T${time}`);

          if (dateTime.isBefore(thirtyMinutesFromNow)) {
            availabilityBeforeThirtyMinutes.timeSlotCollection[0].times.push(
              time,
            );
          } else {
            availabilityAfterThirtyMinutes.timeSlotCollection[0].times.push(
              time,
            );
          }
        });

        if (
          availabilityBeforeThirtyMinutes.timeSlotCollection[0].times.length > 0
        ) {
          groups.now.push(availabilityBeforeThirtyMinutes);
        }

        if (
          availabilityAfterThirtyMinutes.timeSlotCollection[0].times.length > 0
        ) {
          groups.today.push(availabilityAfterThirtyMinutes);
        }
      });

      return groups;
    },
    {
      now: [] as ProviderAvailability[],
      today: [] as ProviderAvailability[],
      tomorrow: [] as ProviderAvailability[],
    },
  );
}
